import type { Geometry, OpenSpec, WindowState } from './types'

import { useCallback, useMemo, useRef, useState } from 'react'

type UseWindows = {
    windows: WindowState[]
    activeUid: string | null
    openWindow: (spec: OpenSpec, place: (natural: { w: number; h: number }, index: number) => Geometry) => void
    focusWindow: (uid: string) => void
    closeWindow: (uid: string) => void
    toggleMinimize: (uid: string) => void
    moveWindow: (uid: string, x: number, y: number) => void
    resizeWindow: (uid: string, w: number, h: number) => void
}

const uidFor = (spec: OpenSpec): string => (spec.projectId ? `project:${spec.projectId}` : spec.app)

export const useWindows = (): UseWindows => {
    const [windows, setWindows] = useState<WindowState[]>([])
    const zRef = useRef(1000)
    const openedRef = useRef(0)

    const openWindow = useCallback<UseWindows['openWindow']>((spec, place) => {
        const uid = uidFor(spec)

        setWindows(prev => {
            const z = ++zRef.current
            const existing = prev.find(item => item.uid === uid)

            if (existing) {
                return prev.map(item => (item.uid === uid ? { ...item, z, minimized: false } : item))
            }

            const geometry = place(spec.natural, openedRef.current++)

            return [
                ...prev,
                {
                    uid,
                    app: spec.app,
                    title: spec.title,
                    icon: spec.icon,
                    projectId: spec.projectId,
                    z,
                    minimized: false,
                    ...geometry
                }
            ]
        })
    }, [])

    const focusWindow = useCallback((uid: string) => {
        setWindows(prev => {
            const target = prev.find(item => item.uid === uid)

            if (!target || target.z === zRef.current) return prev

            const z = ++zRef.current

            return prev.map(item => (item.uid === uid ? { ...item, z } : item))
        })
    }, [])

    const closeWindow = useCallback((uid: string) => {
        setWindows(prev => prev.filter(item => item.uid !== uid))
    }, [])

    const toggleMinimize = useCallback((uid: string) => {
        setWindows(prev => {
            const target = prev.find(item => item.uid === uid)

            if (!target) return prev

            const z = target.minimized ? ++zRef.current : target.z

            return prev.map(item => (item.uid === uid ? { ...item, minimized: !item.minimized, z } : item))
        })
    }, [])

    const moveWindow = useCallback((uid: string, x: number, y: number) => {
        setWindows(prev => prev.map(item => (item.uid === uid ? { ...item, x, y } : item)))
    }, [])

    const resizeWindow = useCallback((uid: string, w: number, h: number) => {
        setWindows(prev => prev.map(item => (item.uid === uid ? { ...item, w, h } : item)))
    }, [])

    const activeUid = useMemo(() => {
        const visible = windows.filter(item => !item.minimized)

        if (visible.length === 0) return null

        return visible.reduce((top, item) => (item.z > top.z ? item : top), visible[0]).uid
    }, [windows])

    return {
        windows,
        activeUid,
        openWindow,
        focusWindow,
        closeWindow,
        toggleMinimize,
        moveWindow,
        resizeWindow
    }
}
